/*
 * @Author: Claude
 * @Date: 2025-12-31
 * @Description: 通用的 Grid 媒体预览项组件（支持图片、音频和视频）
 */
import { Spin } from 'antd';
import Image from 'next/image';
import { FC, memo, useEffect, useMemo, useRef, useState } from 'react';

import { FILE_STATUS } from '../constants';
import { ExtendedUploadFile } from '../types';
import { isAudioFile, isVideoFile } from '../utils/fileTypeDetector';

interface GridMediaPreviewItemProps {
  /** 文件对象 */
  file: ExtendedUploadFile;
  /** 删除回调 */
  onDelete: (e: React.MouseEvent) => void;
  /** 是否可选择 */
  selectable?: boolean;
  /** 选择回调 */
  onSelect?: () => void;
  /** 媒体类型（auto 自动检测，image 图片，audio 音频，video 视频） */
  mediaType?: 'auto' | 'image' | 'audio' | 'video';
}

/**
 * 通用的 Grid 媒体预览项组件
 * 自动根据文件类型或指定的 mediaType 渲染图片、音频或视频预览
 */
export const GridMediaPreviewItem: FC<GridMediaPreviewItemProps> = memo(
  ({ file, onDelete, selectable = false, onSelect, mediaType = 'auto' }) => {
    const { status, url, error, originFileObj, isSelected } = file;
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    // 自动检测媒体类型
    const fileObj = originFileObj || (file as unknown as File);
    const detectedType =
      mediaType === 'auto'
        ? isVideoFile(fileObj)
          ? 'video'
          : isAudioFile(fileObj)
            ? 'audio'
            : 'image'
        : mediaType;

    const previewUrl = useMemo(() => {
      if (url) return url;
      if (originFileObj) {
        return URL.createObjectURL(originFileObj);
      }
      return '';
    }, [detectedType, url, originFileObj]);

    const isUploading = status === FILE_STATUS.UPLOADING;
    const isError = status === FILE_STATUS.ERROR || !!error;

    useEffect(() => {
      if (detectedType !== 'audio') return;

      const audio = audioRef.current;
      if (!audio) return;

      const handleEnded = () => {
        setIsPlaying(false);
      };

      audio.addEventListener('ended', handleEnded);

      return () => {
        audio.pause();
        audio.removeEventListener('ended', handleEnded);
      };
    }, [detectedType, previewUrl]);

    // 处理点击事件
    const handleClick = () => {
      if (selectable && !isUploading && !isError) {
        onSelect?.();
      }
    };

    const handleAudioToggle = async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      if (detectedType !== 'audio' || isUploading || isError) return;

      const audio = audioRef.current;
      if (!audio) return;

      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
        return;
      }

      try {
        await audio.play();
        setIsPlaying(true);
      } catch (playError) {
        console.error('Audio play failed:', playError);
      }
    };

    return (
      <div
        className={`group relative w-full h-[140px] rounded-[8px] overflow-hidden bg-[#f5f5f5] ${
          selectable && !isUploading && !isError
            ? isSelected
              ? 'border-[3px] border-[#4931fa] cursor-pointer'
              : 'border-[1px] border-[#dcdcdc] cursor-pointer hover:border-[#4931fa]'
            : 'border-[1px] border-[#dcdcdc]'
        }`}
        onClick={handleClick}
      >
        {/* 媒体预览 */}
        {previewUrl && detectedType === 'video' && (
          <video
            src={previewUrl}
            className="w-full h-full object-cover"
            controls={false}
            muted
            playsInline
            onMouseEnter={(e) => e.currentTarget.play()}
            onMouseLeave={(e) => {
              e.currentTarget.pause();
              e.currentTarget.currentTime = 0;
            }}
          />
        )}

        {previewUrl && detectedType === 'image' && (
          <Image
            src={previewUrl}
            alt="preview"
            fill
            className="object-cover"
            onError={() => {
              console.error('Image load failed:', previewUrl);
            }}
          />
        )}

        {detectedType === 'audio' && (
          <div className="relative flex h-full w-full items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(73,49,250,0.22),_rgba(244,245,254,0.95)_45%,_#eef1ff_100%)]">
            {previewUrl && (
              <audio
                ref={audioRef}
                src={previewUrl}
                preload="metadata"
                className="hidden"
              />
            )}

            <div className="absolute left-[-20px] top-[-12px] h-24 w-24 rounded-full bg-white/35 blur-xl" />
            <div className="absolute bottom-[-24px] right-[-12px] h-28 w-28 rounded-full bg-[#c8d1ff]/60 blur-2xl" />
            <div
              className={`absolute inset-0 bg-[linear-gradient(180deg,rgba(24,18,51,0.02),rgba(24,18,51,0.14))] transition-opacity duration-200 ${
                isPlaying ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
              }`}
            />
            <div
              className={`pointer-events-none absolute flex items-center justify-center text-[#4931fa] transition-all duration-200 ${
                isPlaying ? 'scale-105 opacity-30' : 'opacity-18 group-hover:opacity-24'
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="84"
                height="84"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M4 9.5C4 8.67157 4.67157 8 5.5 8C6.32843 8 7 8.67157 7 9.5V14.5C7 15.3284 6.32843 16 5.5 16C4.67157 16 4 15.3284 4 14.5V9.5ZM9 6.5C9 5.67157 9.67157 5 10.5 5C11.3284 5 12 5.67157 12 6.5V17.5C12 18.3284 11.3284 19 10.5 19C9.67157 19 9 18.3284 9 17.5V6.5ZM14 9.5C14 8.67157 14.6716 8 15.5 8C16.3284 8 17 8.67157 17 9.5V14.5C17 15.3284 16.3284 16 15.5 16C14.6716 16 14 15.3284 14 14.5V9.5ZM19 11.5C19 10.6716 19.6716 10 20.5 10C21.3284 10 22 10.6716 22 11.5V12.5C22 13.3284 21.3284 14 20.5 14C19.6716 14 19 13.3284 19 12.5V11.5Z"
                  fill="currentColor"
                />
              </svg>
            </div>

            <button
              type="button"
              onClick={handleAudioToggle}
              aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
              disabled={!previewUrl || isUploading || isError}
              className={`relative z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white shadow-[0_10px_28px_rgba(73,49,250,0.14)] transition-all duration-200 ${
                isPlaying
                  ? 'bg-[#4931fa] text-white'
                  : 'bg-white text-[#4931fa] hover:bg-white'
              } ${'opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 focus-visible:opacity-100 focus-visible:scale-100'} ${!previewUrl || isUploading || isError ? 'cursor-not-allowed opacity-0' : ''}`}
            >
              {isPlaying ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
          </div>
        )}

        {!previewUrl && detectedType !== 'audio' && (
          <div className="h-full w-full bg-[#f5f5f5]" />
        )}

        {/* 视频图标指示器（仅视频显示） */}
        {detectedType === 'video' && (
          <div className="absolute bottom-2 left-2 pointer-events-none z-10">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="23 7 16 12 23 17 23 7"></polygon>
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
            </svg>
          </div>
        )}

        {/* 上传中遮罩层 + 加载动画 */}
        {isUploading ? (
          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center z-10">
            <Spin size="large" />
          </div>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(e);
            }}
            className="absolute top-[8px] right-[8px] w-[24px] h-[24px] rounded-[8px] bg-[rgba(0,0,0,0.4)] hover:bg-[rgba(0,0,0,0.7)] flex items-center justify-center z-20 transition-all opacity-0 group-hover:opacity-100"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="13"
              viewBox="0 0 12 13"
              fill="none"
            >
              <path
                d="M7.16667 5.16667V9.83333M4.5 5.16667V9.83333M1.83333 2.5V10.3667C1.83333 11.1134 1.83333 11.4865 1.97866 11.7717C2.10649 12.0226 2.31032 12.227 2.5612 12.3548C2.84614 12.5 3.21933 12.5 3.96461 12.5H7.70206C8.44734 12.5 8.81999 12.5 9.10493 12.3548C9.35581 12.227 9.56032 12.0226 9.68815 11.7717C9.83333 11.4868 9.83333 11.114 9.83333 10.3687V2.5M1.83333 2.5H3.16667M1.83333 2.5H0.5M3.16667 2.5H8.5M3.16667 2.5C3.16667 1.87874 3.16667 1.56827 3.26816 1.32324C3.40349 0.996538 3.66288 0.736819 3.98958 0.601494C4.23461 0.5 4.54541 0.5 5.16667 0.5H6.5C7.12126 0.5 7.43188 0.5 7.67691 0.601494C8.00362 0.736819 8.26311 0.996538 8.39844 1.32324C8.49993 1.56827 8.5 1.87875 8.5 2.5M8.5 2.5H9.83333M9.83333 2.5H11.1667"
                stroke="white"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        {/* 上传失败遮罩层 + 感叹号 + 错误信息 */}
        {isError && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center z-10 p-4">
            <div className="w-[32px] h-[32px] rounded-full bg-[#ff4d4f] flex items-center justify-center mb-2">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            {file.errorMessage && (
              <div className="text-white text-[10px] text-center leading-tight max-w-full break-all">
                {file.errorMessage}
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.file === nextProps.file &&
    prevProps.selectable === nextProps.selectable &&
    prevProps.mediaType === nextProps.mediaType,
);

GridMediaPreviewItem.displayName = 'GridMediaPreviewItem';
